/** @mui/material imports */
import { SvgIconProps } from '@mui/material/SvgIcon';

/** @mui/icons-material imports */
import GpsFixedIcon from '@mui/icons-material/GpsFixed'; // collar data
import Grid4x4Icon from '@mui/icons-material/Grid4x4';
import GradientIcon from '@mui/icons-material/Gradient';
import TuneIcon from '@mui/icons-material/Tune';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import StackedLineIcon from '@mui/icons-material/StackedLineChart';
import PhotoFilterIcon from '@mui/icons-material/PhotoFilter';
import InsightsIcon from '@mui/icons-material/Insights';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings'; // explore logs - visual
import FileDownloadIcon from '@mui/icons-material/FileDownload'; // explore logs - export
import VerifiedIcon from '@mui/icons-material/Verified';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing'; // custom intervals
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'; // Gallery
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'; // upload images
import ViewColumnIcon from '@mui/icons-material/ViewColumn'; // drillholes
import QueryStatsIcon from '@mui/icons-material/QueryStats'; // images core depth summary
import GapIcon from '@mui/icons-material/SpaceBar'; // gaps, overlaps, and unprocessed images
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'; // Run prediction
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'; // Train Model
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark'; // Model Library
import MergeIcon from '@mui/icons-material/MergeType'; // Merge Data
import BlurOnIcon from '@mui/icons-material/BlurOn'; // Smooth Data
import SummarizeIcon from '@mui/icons-material/Summarize';
import ViewInArIcon from '@mui/icons-material/ViewInAr'; // 3D viewer icon
import WorkIcon from '@mui/icons-material/Work'; // project
import PaletteIcon from '@mui/icons-material/Palette'; // color report
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // upload logs
import CollectionsIcon from '@mui/icons-material/Collections'; // download Images
import CalendarViewIcon from '@mui/icons-material/CalendarViewMonth'; // view logs

/** App imports */
import { APP_LINKS } from './constants';
import { D3Icon } from './components/D3Icon';

export interface NavigationItem {
  icon?: React.ComponentType<SvgIconProps>;
  key: string;
  primary: string;
  /** Relative path appended to the project base URL, or a special value:
   *  - `''`         – section-header placeholder (no link)
   *  - `APP_LINKS.*` – external absolute URL
   */
  path: string;
  moduleRequired: string[];
  featureFlagsRequired?: string[];
}

/**
 * Static lookup of all navigation items, indexed by key.
 *
 * Dynamic values (linkTo, accessible, badge) are computed at runtime in the
 * Sidebar component via `getMenuItemByKey`. This map is also intended to be
 * consumed by the router config and route validators.
 */
export const navigationItems: Record<string, NavigationItem> = {
  // Home section
  'project-dashboard': {
    icon: WorkIcon,
    key: 'project-dashboard',
    primary: 'PROJECT',
    path: '/',
    moduleRequired: ['PLATFORM'],
  },
  'project-settings': {
    icon: WorkIcon,
    key: 'project-settings',
    primary: 'PROJECT_SETTINGS',
    path: '/settings',
    moduleRequired: ['PLATFORM'],
  },
  'billing-usage-report': {
    icon: SummarizeIcon,
    key: 'billing-usage-report',
    primary: 'USAGE_REPORT',
    path: '/usage-report',
    moduleRequired: [],
    featureFlagsRequired: ['USAGE_REPORT'],
  },

  // Upload Data section
  'projects-drillholes': {
    icon: ViewColumnIcon,
    key: 'projects-drillholes',
    primary: 'DRILLHOLES',
    path: '/drillholes',
    moduleRequired: ['GALLERY'],
  },
  'projects-upload-images': {
    icon: AddPhotoAlternateIcon,
    key: 'projects-upload-images',
    primary: 'UPLOAD_IMAGES',
    path: '/bulk-upload',
    moduleRequired: ['GALLERY'],
  },
  'projects-collar': {
    icon: GpsFixedIcon,
    key: 'projects-collar',
    primary: 'COLLAR_DATA',
    path: '/collars',
    moduleRequired: ['GALLERY'],
  },
  'add-collar-data': {
    key: 'add-collar-data',
    primary: 'ADD_COLLAR_DATA',
    path: '/collars/add',
    moduleRequired: ['GALLERY'],
  },
  'projects-survey': {
    icon: Grid4x4Icon,
    key: 'projects-survey',
    primary: 'SURVEY_DATA',
    path: '/surveys',
    moduleRequired: ['GALLERY'],
  },
  'add-survey-data': {
    key: 'add-survey-data',
    primary: 'ADD_SURVEY_DATA',
    path: '/surveys/add',
    moduleRequired: ['GALLERY'],
  },
  'projects-interval': {
    icon: FormatLineSpacingIcon,
    key: 'projects-interval',
    primary: 'INTERVAL',
    path: '/intervals',
    moduleRequired: ['GALLERY'],
  },
  'add-custom-intervals': {
    key: 'add-custom-intervals',
    primary: 'ADD_CUSTOM_INTERVALS',
    path: '/intervals/add',
    moduleRequired: ['GALLERY'],
  },
  'projects-add-logs': {
    icon: CloudUploadIcon,
    key: 'projects-add-logs',
    primary: 'UPLOAD_LOGS',
    path: '/upload-logs',
    moduleRequired: ['GALLERY'],
  },

  // Gallery section
  'projects-gallery': {
    icon: PhotoLibraryIcon,
    key: 'projects-gallery',
    primary: 'GALLERY',
    path: '/drillholes/:drillholeId',
    moduleRequired: ['GALLERY'],
  },

  // Data Preparation section
  'projects-images-qa-qc': {
    icon: VerifiedIcon,
    key: 'projects-images-qa-qc',
    primary: 'IMAGES_QA_QC',
    path: '/inconsistencies',
    moduleRequired: ['GALLERY'],
  },
  'projects-images-gaps-overlaps-unprocessed': {
    icon: GapIcon,
    key: 'projects-images-gaps-overlaps-unprocessed',
    primary: 'GAPS_OVERLAPS_UNPROCESSED',
    path: '/inconsistencies',
    moduleRequired: ['GALLERY'],
  },
  'projects-images-core-length': {
    icon: QueryStatsIcon,
    key: 'projects-images-core-length',
    primary: 'IMAGES_CORE_LENGTH_SUMMARY',
    path: '/inconsistencies/images-core-length',
    moduleRequired: ['GALLERY'],
  },
  'projects-cleanimages': {
    icon: PhotoFilterIcon,
    key: 'projects-cleanimages',
    primary: 'CLEAN_IMAGES',
    path: '/clean-images',
    moduleRequired: ['GALLERY'],
  },
  'projects-desurvey': {
    icon: InsightsIcon,
    key: 'projects-desurvey',
    primary: 'DESURVEY',
    path: '/desurvey',
    moduleRequired: ['GALLERY'],
    featureFlagsRequired: ['DESURVEY'],
  },

  // Extractions section - Geologic
  geologic: {
    key: 'geologic',
    primary: 'GEOLOGIC',
    path: '',
    moduleRequired: [],
  },
  'projects-munsell': {
    icon: PaletteIcon,
    key: 'projects-munsell',
    primary: 'STANDARD_COLOR_REPORT',
    path: '/standard-color-report',
    moduleRequired: ['IMAGE_ANALYSIS'],
  },
  'projects-texture': {
    icon: GradientIcon,
    key: 'projects-texture',
    primary: 'TEXTURE_REPORT',
    path: '/texture',
    moduleRequired: ['IMAGE_ANALYSIS'],
  },
  'projects-veinnet': {
    icon: StackedLineIcon,
    key: 'projects-veinnet',
    primary: 'VEIN_DETECTION',
    path: '/veinnet',
    moduleRequired: ['IMAGE_ANALYSIS'],
  },
  'projects-veinfrequency': {
    icon: StackedLineIcon,
    key: 'projects-veinfrequency',
    primary: 'VEIN_FREQUENCY',
    path: '/vein-frequency',
    moduleRequired: ['IMAGE_ANALYSIS'],
  },

  // Extractions section - Geotechnical
  geotechnical: {
    key: 'geotechnical',
    primary: 'GEOTECHNICAL',
    path: '',
    moduleRequired: [],
  },
  'projects-roqenet': {
    icon: TuneIcon,
    key: 'projects-roqenet',
    primary: 'ROCK_COMPOSITION',
    path: '/roqenet',
    moduleRequired: ['IMAGE_ANALYSIS'],
  },
  'projects-rqd': {
    icon: TuneIcon,
    key: 'projects-rqd',
    primary: 'RQD',
    path: '/rqd',
    moduleRequired: ['IMAGE_ANALYSIS'],
  },
  'projects-fracture': {
    icon: TuneIcon,
    key: 'projects-fracture',
    primary: 'FRACTURE_FREQUENCY',
    path: '/fracture-frequency',
    moduleRequired: ['IMAGE_ANALYSIS'],
  },
  'projects-fcnet': {
    icon: BrokenImageIcon,
    key: 'projects-fcnet',
    primary: 'FCNET',
    path: '/fcnet',
    moduleRequired: [],
    featureFlagsRequired: ['FCNET'],
  },

  // Data Processing section
  'merge-data': {
    icon: MergeIcon,
    key: 'merge-data',
    primary: 'MERGE_DATA',
    path: '/merge-data',
    moduleRequired: ['IMAGE_ANALYSIS'],
  },
  'smooth-data': {
    icon: BlurOnIcon,
    key: 'smooth-data',
    primary: 'SMOOTH_DATA',
    path: '/smooth-data',
    moduleRequired: [],
    featureFlagsRequired: ['SMOOTH_DATA'],
  },

  // Data Analysis section
  d3: {
    icon: D3Icon,
    key: 'd3',
    primary: 'D3',
    path: APP_LINKS.d3, // overwritten in sidebar menu with project/company values
    moduleRequired: ['DOMAINING'],
  },
  'prediction-tools': {
    key: 'prediction-tools',
    primary: 'PREDICTION_TOOLS',
    path: '',
    moduleRequired: [],
  },
  'run-prediction': {
    icon: AutoAwesomeIcon,
    key: 'run-prediction',
    primary: 'RUN_PREDICTION',
    path: '/predict',
    moduleRequired: ['IMAGE_ANALYSIS'],
    featureFlagsRequired: ['RUN_PREDICTION'],
  },
  'train-model': {
    icon: FitnessCenterIcon,
    key: 'train-model',
    primary: 'TRAIN_MODEL',
    path: '/train-model',
    moduleRequired: ['IMAGE_ANALYSIS'],
    featureFlagsRequired: ['TRAIN_MODEL'],
  },
  'model-library': {
    icon: CollectionsBookmarkIcon,
    key: 'model-library',
    primary: 'MODEL_LIBRARY',
    path: '/model-library',
    moduleRequired: ['IMAGE_ANALYSIS'],
    featureFlagsRequired: ['MODEL_LIBRARY'],
  },

  // Visualization section
  'projects-visual': {
    icon: DisplaySettingsIcon,
    key: 'projects-visual',
    primary: 'VISUAL',
    path: '/visualizer',
    moduleRequired: ['GALLERY'],
  },
  'projects-visual-v3': {
    icon: DisplaySettingsIcon,
    key: 'projects-visual',
    primary: 'VISUAL',
    path: '/visualizer-v3',
    moduleRequired: ['GALLERY'],
    featureFlagsRequired: ['VISUALIZER_NEW'],
  },
  'test-feature': {
    icon: ViewInArIcon,
    key: 'test-feature',
    primary: 'THREEDVIEWER',
    path: '/3d-viewer',
    moduleRequired: [],
    featureFlagsRequired: ['THREEDVIEWER'],
  },

  // Export section
  'projects-export': {
    icon: FileDownloadIcon,
    key: 'projects-export',
    primary: 'EXPORT',
    path: '/export',
    moduleRequired: ['GALLERY'],
  },
  'projects-download-images': {
    icon: CollectionsIcon,
    key: 'projects-download-images',
    primary: 'DOWNLOAD_IMAGES',
    path: '/download-images',
    moduleRequired: ['GALLERY'],
  },
  'projects-view-logs': {
    icon: CalendarViewIcon,
    key: 'projects-view-logs',
    primary: 'DATA_TABLE',
    path: '/view-logs',
    moduleRequired: ['GALLERY'],
  },
};
